import { Button, Box, Flex, Icon, Text, useColorModeValue, Spinner, Select, Center } from "@chakra-ui/react";
import Card from "components/card/Card.js";
import LineChart from "components/charts/LineChart";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "../../../../constants";
import { getAuthToken } from "../../../../helpers/helpers";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";


export const lineChartOptionsTotalSpent = {
  chart: {
    toolbar: {
      show: false,
    },
    dropShadow: {
      enabled: true,
      top: 13,
      left: 0,
      blur: 10,
      opacity: 0.1,
      color: "#4318FF",
    },
  },
  colors: ["#4318FF", "#39B8FF"],
  markers: {
    size: 0,
    colors: "white",
    strokeColors: "#7551FF",
    strokeWidth: 3,
    strokeOpacity: 0.9,
    strokeDashArray: 0,
    fillOpacity: 1,
    discrete: [],
    shape: "circle",
    radius: 2,
    offsetX: 0,
    offsetY: 0,
    showNullDataPoints: true,
  },
  tooltip: {
    theme: "dark",
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: "smooth",
    type: "line",
  },
  xaxis: {
    type: "valeric",
    categories: [
      "JAN",
      "FEV",
      "MAR",
      "AVR",
      "MAI",
      "JUIN",
      "JUIL",
      "AOU",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ],
    labels: {
      style: {
        colors: "#A3AED0",
        fontSize: "12px",
        fontWeight: "500",
      },
    },
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
  },
  yaxis: {
    show: false,
  },
  legend: {
    show: false,
  },
  grid: {
    show: false,
    column: {
      color: ["#7551FF", "#39B8FF"],
      opacity: 0.5,
    },
  },
  color: ["#7551FF", "#39B8FF"],
};

export default function TotalSpent({ startDate, endDate, ...rest }) {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const [count, setCount] = useState(0);
  const [monthlyCounts, setMonthlyCounts] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [monthlyPoints, setMonthlyPoints] = useState([]);
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [startMonth, setStartMonth] = useState(new Date().getMonth() - 11); // Show last 12 months
  const [selectedOperateur, setSelectedOperateur] = useState("all");
  const [uniqueOperateurs, setUniqueOperateurs] = useState([]);
  const [operatorNames, setOperatorNames] = useState({});
  const [isLoadingOperators, setIsLoadingOperators] = useState(false);
  const [selectedCurve, setSelectedCurve] = useState("all");
  const [isLoading, setIsLoading] = useState(true);

  const months = [
    "JAN", "FEV", "MAR", "AVR", "MAI", "JUIN", "JUIL", "AOU", "SEP", "OCT", "NOV", "DEC",
  ];

  const safeDate = (date) => {
    const parsedDate = new Date(date);
    return isNaN(parsedDate) ? new Date() : parsedDate;
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const token = getAuthToken();

// Fetch the data from both APIs
      const response1 = axios
        .get(`${API_URL}/api/track/action/AFFICHER_TRAITEMENT`, {
          headers: { 
            "x-access-token": token,
            Authorization: `Bearer ${token}`,
           },
        })
        .catch((error) => {
          console.error("Error fetching AFFICHER_TRAITEMENT:", error);
          return { data: [] };
        });

      const response2 = axios
        .get(`${API_URL}/api/cart`, {
          headers: { 
            "x-access-token": token,
            Authorization: `Bearer ${token}`,
           },
        })
        .catch((error) => {
          console.error("Error fetching cart:", error);
          return { data: [] };
        });

      try {
        const [res1, res2] = await Promise.all([response1, response2]);

        const operateursFromRes1 = res1.data.map(item => item.code_operateur).filter(Boolean);
        const operateursFromRes2 = res2.data.map(item => item.code_operateur).filter(Boolean);
        const allOperateurs = [...new Set([...operateursFromRes1, ...operateursFromRes2])];

        setUniqueOperateurs(allOperateurs);

        const fetchOperatorNames = async (codeOperateur) => {
          try {
            const token = getAuthToken();
            const userId = localStorage.getItem('UserID'); 
        
            const response = await axios.post(
              `${API_URL}/api/profile/${userId}`, 
              { code_operateur: codeOperateur }, 
              {
                headers: { 
                  "x-access-token": token,
                  Authorization: `Bearer ${token}`,
                },
              }
            );
        
            return response.data.nom_operateur; 
          } catch (error) {
            console.error(`Error fetching profile for code_operateur ${codeOperateur}:`, error);
            return null;
          }
        };

        const fetchAllOperatorNames = async () => {
          setIsLoadingOperators(true);
          const names = {};
          for (const codeOperateur of allOperateurs) {
            const nomOperateur = await fetchOperatorNames(codeOperateur);
            if (nomOperateur) {
              names[codeOperateur] = nomOperateur;
            }
          }
          setOperatorNames(names);
          setIsLoadingOperators(false);
        };

        fetchAllOperatorNames();

        const isInDateRange = (itemDate) => {
          if (startDate && endDate) {
            const date = safeDate(itemDate);
            const start = new Date(startDate);
            const end = new Date(endDate);
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
            return date >= start && date <= end;
          }
          return true;
        };

        const filteredData1 = res1.data.filter((item) => {
          const isOperateurMatch = selectedOperateur === "all" || 
            (selectedOperateur === "admin" && !item.code_operateur) || 
            item.code_operateur === selectedOperateur;
          return isInDateRange(item.created_at) && isOperateurMatch;
        });

        const filteredData2 = res2.data.filter((item) => {
          const isOperateurMatch = selectedOperateur === "all" || 
            (selectedOperateur === "admin" && !item.code_operateur) || 
            item.code_operateur === selectedOperateur;
          return isInDateRange(item.created_at) && isOperateurMatch;
        });

        const counts = Array(12).fill(0);
        const revenue = Array(12).fill(0);
        const points = Array(12).fill(0);

        filteredData1.forEach((item) => {
          const date = new Date(item.created_at);
          const monthIndex = (date.getFullYear() - startYear) * 12 + date.getMonth() - startMonth;
          if (monthIndex >= 0 && monthIndex < 12) {
            counts[monthIndex]++;
          }
        });

        filteredData2.forEach((item) => {
          const date = new Date(item.created_at);
          const monthIndex = (date.getFullYear() - startYear) * 12 + date.getMonth() - startMonth;
          if (monthIndex >= 0 && monthIndex < 12) {
            item.items.forEach((item) => {
              if (item.medicamentDetails && item.medicamentDetails.prix !== undefined && item.medicamentDetails.prix !== null) {
                const prix = parseFloat(item.medicamentDetails.prix) || 0;
                const quantity = parseFloat(item.quantity) || 0;
                revenue[monthIndex] += prix * quantity;
              }
              if (item.point) {
                points[monthIndex] = (points[monthIndex] || 0) + item.point;
              }
            });
          }
        });

        setMonthlyCounts(counts);
        
        revenue.forEach((x, i, arr) => arr[i] = Math.round(x));
        setMonthlyRevenue(revenue);
        
        setMonthlyPoints(points);
        
        setCount(filteredData1.length);
        setIsLoading(false);
      } catch (error) {
        console.error("Error making the request:", error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, startYear, startMonth, selectedOperateur]);

  const handlePrevious = () => {
    setStartMonth((prev) => {
      if (prev === 0) {
        setStartYear((year) => year - 1);
        return 11;
      }
      return prev - 1;
    });
  };

  const handleNext = () => {
    setStartMonth((prev) => {
      if (prev === 11) {
        setStartYear((year) => year + 1);
        return 0;
      }
      return prev + 1;
    });
  };

  const lineChartDataTotalSpent = [
    {
      name: "Revenues",
      data: monthlyRevenue,
      color: "#7551FF",
    },
    {
      name: "Utilisations",
      data: monthlyCounts,
      color: "#39B8FF",
    },
    {
      name: "Points",
      data: monthlyPoints,
      color: "#FF0000",
    },
  ];

  const filteredChartData = selectedCurve === "all"
    ? lineChartDataTotalSpent
    : lineChartDataTotalSpent.filter((curve) => curve.name === selectedCurve);

  const displayedMonths = [...Array(12)].map((_, i) => {
    const date = new Date(startYear, startMonth + i);
    return months[date.getMonth()] + " " + date.getFullYear();
  });

  if (isLoading) {
    return (
      <Card
        justifyContent="center"
        align="center"
        direction="column"
        w="100%"
        mb="0px"
        minH="400px"
        {...rest}
      >
        <Flex justify="space-between" ps="0px" pe="20px" pt="5px" w="100%">
          <Flex align="center" w="100%">
            <Text
              me="auto"
              color={textColor}
              fontSize="xl"
              fontWeight="700"
              lineHeight="100%"
            >
              Aperçu des performances
            </Text>
          </Flex>
        </Flex>
        <Center h="100%" minH="350px">
          <Flex direction="column" align="center" gap="4">
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="brand.500"
              size="xl"
            />
            <Text color="secondaryGray.600" fontSize="sm" fontWeight="500">
              Chargement des données de performance...
            </Text>
          </Flex>
        </Center>
      </Card>
    );
  }

  return (
    <Card
      justifyContent="center"
      align="center"
      direction="column"
      w="100%"
      mb="0px"
      {...rest}
    >
      <Flex justify="space-between" ps="0px" pe="20px" pt="5px">
        <Flex align="center" w="100%">
          <Text
            me="auto"
            color={textColor}
            fontSize="xl"
            fontWeight="700"
            lineHeight="100%"
          >
            Aperçu des performances
          </Text>
          <Select
            value={selectedCurve}
            onChange={(e) => setSelectedCurve(e.target.value)}
            size="sm"
            width="150px"
            ml="10px"
          >
            <option value="all">Tous</option>
            <option value="Revenues">Revenues</option>
            <option value="Utilisations">Utilisations</option>
            <option value="Points">Points</option>
          </Select>
        </Flex>
      </Flex>
      <Flex w="100%" flexDirection={{ base: "column", lg: "row" }}>
        <Flex flexDirection="column" me="20px" mt="28px" align="center">
          <Text
            color={textColor}
            fontSize="34px"
            textAlign="center"
            fontWeight="700"
            lineHeight="100%"
          >
            {selectedCurve === "Utilisations" ? count : 
            selectedCurve === "Revenues" ? Math.round(monthlyRevenue.reduce((a, b) => (a || 0) + (b || 0), 0)) : 
            selectedCurve === "Points" ? monthlyPoints.reduce((a, b) => (a || 0) + (b || 0), 0) : ""}
          </Text>
          <Flex align="center" mb="20px" justify="center">
            <Text
              color="secondaryGray.600"
              fontSize="sm"
              fontWeight="500"
              mt="4px"
              me="4px"
            >
              {selectedCurve === "Utilisations" ? "Nombre d'utilisations" : 
               selectedCurve === "Revenues" ? "Revenues" : 
               selectedCurve === "Points" ? "Nombre de points" : ""}
            </Text>
          </Flex>
        </Flex>
        <Box minH="260px" w="100%" mt="auto">
          {monthlyCounts.length > 0 && monthlyRevenue.length > 0 && (
            <LineChart
              chartData={filteredChartData}
              chartOptions={{
                ...lineChartOptionsTotalSpent,
                xaxis: { ...lineChartOptionsTotalSpent.xaxis, categories: displayedMonths },
              }}
            />
          )}
          <Flex justify="space-between" w="100%" gap="10px">
            <Button
              onClick={handlePrevious}
              bg="gray.300"
              color="white"
              _hover={{ bg: "gray.400" }}
              _active={{ bg: "gray.500" }}
              borderRadius="20px"
              px="10px"
              minW="40px"
            >
              <Icon as={ChevronLeftIcon} boxSize={5} color="white" />
            </Button>

            <Button
              onClick={handleNext}
              bg="gray.300"
              color="white"
              _hover={{ bg: "gray.400" }}
              _active={{ bg: "gray.500" }}
              borderRadius="20px"
              px="10px"
              minW="40px"
            >
              <Icon as={ChevronRightIcon} boxSize={5} color="white" />
            </Button>
          </Flex>
        </Box>
      </Flex>
      <Flex justify="center" mt="10px" gap="10px" flexWrap="wrap">
        {isLoadingOperators ? (
          <Spinner size="md" />
        ) : (
          <>
            <Button
              onClick={() => setSelectedOperateur("all")}
              bg={selectedOperateur === "all" ? "brand.500" : "gray.300"}
              color={selectedOperateur === "all" ? "white" : "gray.700"}
              _hover={{ bg: "brand.600" }}
              minW="auto"
              px="20px"
              mb="5px"
            >
              All
            </Button>
            {uniqueOperateurs
              .filter((operateur) => operatorNames[operateur]) // Don't show deleted profiles
              .map((operateur) => (
                <Button
                  key={operateur}
                  onClick={() => setSelectedOperateur(operateur)}
                  bg={selectedOperateur === operateur ? "brand.500" : "gray.300"}
                  color={selectedOperateur === operateur ? "white" : "gray.700"}
                  _hover={{ bg: "brand.600" }}
                  minW="auto"
                  px="20px"
                  mb="5px"
                >
                  {operatorNames[operateur]}
                </Button>
              ))}
          </>
        )}
      </Flex>
    </Card>
  );
}
